/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { cssMap as cssMapCompiled } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { Label } from '@atlaskit/form';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { ButtonItem } from '@atlaskit/menu';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	toggleContainer: {
		flexShrink: 0,
	},
	contentContainer: {
		whiteSpace: 'normal',
		// Prevent double event trigger when clicking on the toggle
		pointerEvents: 'none',
	},
	alphaChip: {
		borderRadius: token('radius.small'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.discovery.bold'),
	},
});

/* eslint-disable  @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors */
const compiledStyles = cssMapCompiled({
	roundedItem: {
		borderRadius: '8px',
		overflow: 'hidden',
	},
	forcePointer: {
		cursor: 'pointer',
		'& label': {
			cursor: 'pointer',
		},
	},
	forceNotAllowed: {
		cursor: 'not-allowed',
		'&  label': {
			cursor: 'not-allowed',
		},
	},
});
/* eslint-enable  @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors */

export const ChatModeToggleMenuItem = ({
	toggleId,
	icon,
	isCheckmark = false,
	title,
	lozengeLabel,
	subtitle,
	onToggle,
	isActive,
	isDisabled,
}: {
	toggleId: string;
	icon: React.ReactNode;
	isCheckmark?: boolean;
	title: string;
	lozengeLabel?: string;
	subtitle?: string;
	onToggle: (active: boolean) => void;
	isActive: boolean;
	isDisabled: boolean;
}): JSX.Element => {
	return (
		<ButtonItem
			role="menuitem"
			isDisabled={isDisabled}
			onClick={() => onToggle(!isActive)}
			iconBefore={icon}
			css={[
				compiledStyles.roundedItem,
				isDisabled ? compiledStyles.forceNotAllowed : compiledStyles.forcePointer,
			]}
		>
			<Inline
				alignBlock="center"
				spread="space-between"
				space="space.200"
				xcss={styles.contentContainer}
			>
				<Label htmlFor={toggleId}>
					<Stack space="space.050">
						<Inline alignBlock="center" space="space.100">
							<Text size="medium" weight="medium">
								{title}
							</Text>
							{lozengeLabel ? (
								<Inline alignBlock="center" xcss={styles.alphaChip}>
									<Text size="small" weight="bold" color="color.text.inverse">
										{lozengeLabel}
									</Text>
								</Inline>
							) : undefined}
						</Inline>
						{subtitle ? (
							<Text size="small" weight="regular" color="color.text.subtle">
								{subtitle}
							</Text>
						) : undefined}
					</Stack>
				</Label>
				<div css={styles.toggleContainer}>
					{isCheckmark ? (
						isActive ? (
							<CheckMarkIcon label="" />
						) : undefined
					) : (
						<Toggle id={toggleId} defaultChecked={isActive} isChecked={isActive} />
					)}
				</div>
			</Inline>
		</ButtonItem>
	);
};
